import { useState, useEffect, useRef } from "react";
import { Search, Package, FileText, Sparkles, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useNavigate } from "react-router-dom";

export default function SpotlightSearch({ open, onClose }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 30);
    if (!open) {
      setQ("");
      setResults([]);
      setActive(0);
    }
  }, [open]);

  useEffect(() => {
    if (!q.trim()) {
      setResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/search", { params: { q } });
        setResults(data);
        setActive(0);
      } finally {
        setLoading(false);
      }
    }, 180);
    return () => clearTimeout(t);
  }, [q]);

  function onKey(e) {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter" && results[active]) { go(results[active]); }
    else if (e.key === "Escape") { onClose(); }
  }

  function go(r) {
    onClose();
    nav(r.link);
  }

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh] px-4" onClick={onClose} data-testid="spotlight-overlay">
      <div className="absolute inset-0 bg-background" />
      <div
        className="relative w-full max-w-2xl bg-surface border border-border rounded-sm shadow-2xl spotlight-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 px-5 py-4 border-b border-border">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKey}
            placeholder="Search anything in Apollo…"
            className="flex-1 bg-transparent outline-none text-base placeholder:text-muted-foreground"
            data-testid="spotlight-input"
          />
          <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">ESC</span>
        </div>

        <div className="max-h-[50vh] overflow-y-auto">
          {q && !loading && results.length === 0 && (
            <div className="px-5 py-10 text-center text-sm text-muted-foreground" data-testid="spotlight-empty">
              No results for &quot;{q}&quot;
            </div>
          )}
          {!q && (
            <div className="p-5 text-xs text-muted-foreground space-y-2">
              <div className="font-mono uppercase tracking-[0.3em]">Try searching</div>
              <div className="flex flex-wrap gap-2">
                {["Blue Dream", "Passion Fruit Candy", "MA menu", "Instagram", "Logo", "Sales deck"].map((s) => (
                  <button key={s} onClick={() => setQ(s)} className="px-2.5 py-1 text-xs border border-border rounded-sm hover:bg-surface-elev transition">{s}</button>
                ))}
              </div>
            </div>
          )}
          {results.map((r, i) => (
            <button
              key={`${r.type}-${r.id}`}
              onClick={() => go(r)}
              className={`w-full text-left px-5 py-3 flex items-center gap-4 border-b border-border last:border-b-0 transition
                ${i === active ? "bg-surface-elev" : "hover:bg-surface-elev"}`}
              data-testid={`spotlight-result-${i}`}
            >
              <div className="w-10 h-10 rounded-sm bg-muted overflow-hidden flex items-center justify-center flex-shrink-0">
                {r.image ? <img src={r.image} alt="" className="w-full h-full object-cover" /> : <TypeIcon type={r.type} />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{r.title}</div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{r.subtitle}</div>
              </div>
              <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function TypeIcon({ type }) {
  const Icon = type === "product" ? Package : type === "campaign" ? Sparkles : FileText;
  return <Icon className="h-4 w-4 text-muted-foreground" />;
}
